import React, { useState, useMemo } from "react";
import "react-tabs/style/react-tabs.css";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import useGetStructuredNoteDetails from "../../../../hooks/useGetStructuredNoteDetails";
import { toast } from "react-toastify";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { StructuredNoteDetailsData } from "../../../../services/getStructuredNoteDetails";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

const renderDateCell = (value: string | null | undefined) => {
  return value ? <span>{convertDate(value)}</span> : <div>&#8210;</div>;
};

const renderNumberCell = (value: number | null | undefined) => {
  return value !== undefined && value !== null && !isNaN(Number(value)) ? (
    formatNumbersWithThousandSeperators(Number(value), true)
  ) : (
    <div>&#8210;</div>
  );
};

const StructuredNoteDetails = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const orgRoleId = useSelector((state: RootState) => state.auth.orgRoleId);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "issuedAt", desc: true },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, error, isLoading } = useGetStructuredNoteDetails(
    token,
    orgRoleId!,
    pagination,
    apiColumnFilters,
    apiSorting,
    globalFilter
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  const columns = useMemo<MRT_ColumnDef<StructuredNoteDetailsData>[]>(
    () => [
      {
        accessorKey: "noteId",
        header: "Note ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "status",
        header: "Note Status",
        filterFn: "equals",
        mantineFilterSelectProps: {
          data: ["active", "closed"] as string[],
        },
        filterVariant: "select",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "tenure",
        header: "Tenure (Days)",
        filterVariant: "range",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "issuedAt",
        header: "Note Issuance Date",
        filterVariant: "date-range",
        Cell: ({ cell }) => renderDateCell(cell.getValue<string>()),
      },
      {
        accessorKey: "maturityDate",
        header: "Maturity Date",
        filterVariant: "date-range",
        Cell: ({ cell }) => renderDateCell(cell.getValue<string>()),
      },
      {
        accessorKey: "investedPrincipal",
        header: "Principal (Invested)",
        filterVariant: "range",
        Cell: ({ cell }) => renderNumberCell(cell.getValue<number>()),
      },
      {
        accessorKey: "repaidPrincipal",
        header: "Principal (Repaid)",
        filterVariant: "range",
        Cell: ({ cell }) => renderNumberCell(cell.getValue<number>()),
      },
      {
        accessorKey: "expectedInterest",
        header: "Net Interest (Expected)",
        filterVariant: "range",
        Cell: ({ cell }) => renderNumberCell(cell.getValue<number>()),
      },
      {
        accessorKey: "repaidInterest",
        header: "Net Interest (Repaid)",
        filterVariant: "range",
        Cell: ({ cell }) => renderNumberCell(cell.getValue<number>()),
      },
      {
        accessorKey: "repaidAdditionalInterest",
        header: "Additional Interest (Repaid)",
        filterVariant: "range",
        Cell: ({ cell }) => renderNumberCell(cell.getValue<number>()),
      },
      {
        accessorKey: "totalMerchants",
        header: "Total Number of Merchant",
        filterVariant: "range",
      },
      {
        accessorKey: "totalSKU",
        header: "Total Number of Stock Keeping Unit (SKU)",
        filterVariant: "range",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: {
      pagination,
      isLoading,
      sorting,
      globalFilter,
      columnFilters,
    }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
  });

  return (
    <>
      <div className="RestPage">
        <div className="container-fluid">
          <div>
            <MantineReactTable table={table} />
          </div>
        </div>

        <p className="note">
          Net Interest: Interest received by the Investor, net of platform fees.
          <br />
          Additional Interest: Interest received after the Note&apos;s maturity
          date, net of platform fees.
        </p>
      </div>
    </>
  );
};

export default StructuredNoteDetails;
